import { useCallback, useEffect, useRef, useState } from 'react';

import { useUpdateMandate } from '../models/mutations';
import { toMandateForm, toMandatePayload } from '../utils/mandatePayload';

const SAVE_DELAY_MS = 400;

/**
 * The limit chips of one conversation. The chips answer at once; the server gets the change
 * after a short pause, and in any case before anything that depends on it. `flush()` is the
 * persistence boundary: it resolves `true` only when the server holds the run's latest change
 * made before the call (or a newer one), `false` when that change was refused or dropped
 * (chips back on the server's limits). Every change carries a version; a write records the
 * version it saved for its run, so a flush judges its own change, never an earlier or another
 * run's outcome. Writes are serialised; a change made during a write is written right after
 * it. `pending` stays true until the server holds what the chips show.
 *
 * @param {string|null} runId
 * @param {object|null} serverMandate - The mandate of the run snapshot
 * @returns {{form: object, change: function(object): void, flush: function(): Promise<boolean>, pending: boolean}}
 */
export function useMandateChips(runId, serverMandate) {
	const updateMandate = useUpdateMandate();
	const [form, setForm] = useState(() => toMandateForm(serverMandate));
	const [pending, setPending] = useState(false);
	const pendingRef = useRef(null);
	const chainRef = useRef(null);
	const versionRef = useRef(0);
	// Per run: the version of its latest change and the newest version the server confirmed.
	const changedRef = useRef(new Map());
	const savedRef = useRef(new Map());
	const timerRef = useRef(null);
	const runIdRef = useRef(runId);
	runIdRef.current = runId;
	const serverMandateRef = useRef(serverMandate);
	serverMandateRef.current = serverMandate;

	const mutateAsync = updateMandate.mutateAsync;
	const writePending = useCallback(async () => {
		while (pendingRef.current) {
			const change = pendingRef.current;
			pendingRef.current = null;
			if (!change.runId) continue;
			try {
				await mutateAsync({ runId: change.runId, mandate: toMandatePayload(change.form) });
				const saved = savedRef.current.get(change.runId) ?? 0;
				savedRef.current.set(change.runId, Math.max(saved, change.version));
			} catch {
				// Reported by the global MutationCache. The chips must never show limits the server
				// refused: back to what it holds, and nothing else of this run is written on top.
				if (pendingRef.current?.runId === change.runId) pendingRef.current = null;
				if (change.runId === runIdRef.current) setForm(toMandateForm(serverMandateRef.current));
				// Flushes already waiting keep their target and fail; later ones start from the
				// server's limits the chips now show again.
				changedRef.current.delete(change.runId);
			}
		}
	}, [mutateAsync]);

	const flush = useCallback(() => {
		clearTimeout(timerRef.current);
		const flushRunId = runIdRef.current;
		const target = changedRef.current.get(flushRunId) ?? 0;
		const write = (chainRef.current ?? Promise.resolve()).then(writePending);
		chainRef.current = write;
		write.finally(() => {
			if (chainRef.current !== write) return;
			chainRef.current = null;
			setPending(Boolean(pendingRef.current));
		});
		return write.then(() => (savedRef.current.get(flushRunId) ?? 0) >= target);
	}, [writePending]);

	// The server's limits win whenever nothing of this run is pending (another tab, a proposal).
	// A new run always starts from its own limits, whatever the old run still writes.
	const formRunRef = useRef(runId);
	useEffect(() => {
		const switched = formRunRef.current !== runId;
		formRunRef.current = runId;
		if (switched || (!pendingRef.current && !chainRef.current)) setForm(toMandateForm(serverMandate));
	}, [runId, serverMandate]);

	// Leaving the run or the chat persists what is still pending for the run it belongs to.
	useEffect(() => () => void flush(), [runId, flush]);

	const change = useCallback(
		(patch) => {
			// Without a run the chips are the mandate the draft is created with: nothing to write.
			if (!runId) {
				setForm((current) => ({ ...current, ...patch }));
				return;
			}
			setForm((current) => {
				const next = { ...current, ...patch };
				versionRef.current += 1;
				changedRef.current.set(runId, versionRef.current);
				pendingRef.current = { runId, form: next, version: versionRef.current };
				return next;
			});
			setPending(true);
			clearTimeout(timerRef.current);
			timerRef.current = setTimeout(flush, SAVE_DELAY_MS);
		},
		[runId, flush],
	);

	return { form, change, flush, pending };
}
